// frontend/src/components/FAQ.js
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, HelpCircle, Home, Plane, Gift, Calendar, MapPin, Users } from 'lucide-react';
import GiftBankDetails from './GiftBankDetails';

const FAQ = () => {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    { icon: Calendar, question: t('faq.question1'), answer: t('faq.answer1') },
    { icon: MapPin,  question: t('faq.question2'), answer: t('faq.answer2') },
    { icon: Home,    question: t('faq.question3'), answer: t('faq.answer3') },
    { icon: Plane,   question: t('faq.question4'), answer: t('faq.answer4') },
    { icon: Users,   question: t('faq.question5'), answer: t('faq.answer5') },
    { icon: Gift,    question: t('faq.question6'), answer: <GiftBankDetails /> },
  ];

  const toggleFAQ = (index) => setOpenIndex(openIndex === index ? null : index);

  return (
    <section id="faq" className="py-20 bg-gradient-to-b from-amber-50 to-stone-100">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <div className="bg-gray-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <HelpCircle className="w-8 h-8 text-gray-600" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-800">{t('faq.title')}</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">{t('faq.subtitle')}</p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => {
            const Icon = faq.icon;
            const isOpen = openIndex === index;

            return (
              <div
                key={index}
                className="bg-white rounded-xl shadow-lg overflow-hidden transition-shadow duration-300 hover:shadow-xl"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-5 flex items-center justify-between focus:outline-none focus:bg-gray-50 transition-colors"
                  aria-expanded={isOpen}
                  aria-controls={`faq-panel-${index}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-lg transition-colors ${isOpen ? 'bg-indigo-100' : 'bg-gray-100'}`}>
                      <Icon className={`w-5 h-5 ${isOpen ? 'text-indigo-600' : 'text-gray-600'}`} />
                    </div>
                    <h3 className="text-left font-semibold text-gray-800 text-lg">{faq.question}</h3>
                  </div>
                  <ChevronDown
                    className={`w-5 h-5 text-gray-500 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                {/* Auto-height accordion: grid-rows animates from 0fr -> 1fr */}
                <div
                  id={`faq-panel-${index}`}
                  className={`px-6 grid transition-all duration-300 ${
                    isOpen ? 'grid-rows-[1fr] py-4' : 'grid-rows-[0fr]'
                  }`}
                >
                  <div className="overflow-hidden pl-14 pr-4 text-gray-600 leading-relaxed">
                    {typeof faq.answer === 'string' ? (
                      <div dangerouslySetInnerHTML={{ __html: faq.answer }} />
                    ) : (
                      faq.answer
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-10 text-center">
          <p className="text-gray-600 mb-4">{t('faq.moreQuestions')}</p>
          <a
            href={`mailto:contactveenco@gmail.com?subject=${encodeURIComponent(t('faq.emailSubject'))}`}
            className="inline-flex items-center gap-2 bg-gray-900 text-white font-semibold py-3 px-6 rounded-xl hover:bg-black transition"
          >
            {t('faq.contactUs')}
          </a>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
